/**
 * Admin API module
 *
 * All admin endpoints require @admin_required on the backend.
 * This provides an additional layer of frontend security.
 */

import { get, post, put, del, patch } from './client';
import type { SystemPrompt, ABTest, ABTestResponse, QAStats, QAFlag } from '../types';

// ============================================
// System Prompts
// ============================================

export async function getPrompts(): Promise<{ prompts: SystemPrompt[] }> {
  return get('/api/admin/prompts');
}

export async function getPrompt(id: number): Promise<{ prompt: SystemPrompt }> {
  return get(`/api/admin/prompts/${id}`);
}

export async function createPrompt(data: {
  name: string;
  description?: string;
  content: string;
  is_default?: boolean;
}): Promise<{ prompt: SystemPrompt }> {
  return post('/api/admin/prompts', data);
}

export async function updatePrompt(
  id: number,
  data: {
    name?: string;
    description?: string;
    content?: string;
    is_default?: boolean;
  }
): Promise<{ prompt: SystemPrompt }> {
  return put(`/api/admin/prompts/${id}`, data);
}

export async function deletePrompt(id: number): Promise<{ success: boolean }> {
  return del(`/api/admin/prompts/${id}`);
}

// ============================================
// A/B Tests
// ============================================

export async function getABTests(): Promise<{ tests: ABTest[] }> {
  return get('/api/admin/ab-tests');
}

export async function getABTest(id: number): Promise<{ test: ABTest }> {
  return get(`/api/admin/ab-tests/${id}`);
}

export async function createABTest(data: {
  name: string;
  description?: string;
  prompt_a_id: number;
  prompt_b_id: number;
  traffic_percentage?: number;
}): Promise<{ test: ABTest }> {
  return post('/api/admin/ab-tests', data);
}

export async function updateABTest(
  id: number,
  data: {
    name?: string;
    description?: string;
    prompt_a_id?: number;
    prompt_b_id?: number;
    traffic_percentage?: number;
  }
): Promise<{ test: ABTest }> {
  return put(`/api/admin/ab-tests/${id}`, data);
}

export async function activateABTest(id: number): Promise<{ success: boolean }> {
  return post(`/api/admin/ab-tests/${id}/activate`);
}

export async function deactivateABTest(id: number): Promise<{ success: boolean }> {
  return post(`/api/admin/ab-tests/${id}/deactivate`);
}

export async function deleteABTest(id: number): Promise<{ success: boolean }> {
  return del(`/api/admin/ab-tests/${id}`);
}

export async function getABTestResponses(id: number): Promise<{ responses: ABTestResponse[] }> {
  return get(`/api/admin/ab-tests/${id}/responses`);
}

// ============================================
// QA Stats & Flags
// ============================================

export async function getQAStats(): Promise<QAStats> {
  return get('/api/admin/qa/stats');
}

export async function getQAFlags(): Promise<{ flags: QAFlag[] }> {
  return get('/api/admin/qa/flags');
}

export async function updateQAFlag(
  id: number,
  data: { status: 'new' | 'reviewed' | 'fixed' }
): Promise<{ flag: QAFlag }> {
  return patch(`/api/admin/qa/flags/${id}`, data);
}

export async function runQAReview(sessionUuid: string): Promise<{ success: boolean; result: unknown }> {
  return post('/api/admin/qa/run', { session_uuid: sessionUuid });
}
